// src/components/AuthFlow.jsx
import React, { useState } from 'react';
import { Input, Button, message, Card, Typography, Space, Select, Divider } from 'antd';
import { CloseOutlined, MobileOutlined, MailOutlined } from '@ant-design/icons';
import axios from 'axios';
import configFile from './config';
import { motion } from 'framer-motion';

const { Title, Text } = Typography;
const { Option } = Select;
const apiURL = configFile.API_URL;

const AuthFlow = ({ onSuccess, onCancel, config }) => {
  const [step, setStep] = useState('input');
  const [method, setMethod] = useState('phone');
  const [value, setValue] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [countryCode, setCountryCode] = useState('+91');
  const isDev = process.env.NODE_ENV !== 'production';

  const validateInput = () => {
    if (method === 'phone') return /^\d{10}$/.test(value);
    if (method === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    return false;
  };

  const sendOTP = async () => {
    if (!validateInput()) return message.error(`Please enter a valid ${method}`);
    const fullValue = method === 'phone' ? `${countryCode}${value}` : value;
    setLoading(true);
    try {
      if (isDev) {
        message.success('OTP: 123456 (dev mode)');
        setStep('otp');
      } else {
        await axios.post(`${apiURL}/pos/auth/send-otp`, {
          [method === 'phone' ? 'contact_number' : 'email']: fullValue,
        });
        message.success(`OTP sent to your ${method}`);
        setStep('otp');
      }
    } catch (e) {
      message.error(e.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (isDev && otp === '123456') {
      localStorage.setItem('verifiedContact', JSON.stringify({ method, value, timestamp: new Date().toISOString() }));
      localStorage.setItem('token', 'dev-token-1234');
      message.success('Login successful (dev mode)');
      onSuccess();
      return;
    }
    if (otp.length !== 6) return message.error('Please enter a 6-digit OTP');
    const fullValue = method === 'phone' ? `${countryCode}${value}` : value;
    setLoading(true);
    try {
      const { data } = await axios.post(`${apiURL}/pos/auth/verify-otp`, {
        [method === 'phone' ? 'contact_number' : 'email']: fullValue,
        otp,
      });
      localStorage.setItem('verifiedContact', JSON.stringify({ method, value: fullValue, timestamp: new Date().toISOString() }));
      localStorage.setItem('token', data.token);
      message.success('Login successful');
      onSuccess();
    } catch (e) {
      message.error(e.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const switchMethod = () => {
    setMethod(method === 'phone' ? 'email' : 'phone');
    setValue('');
  };

  const primaryColor = config.colors?.primary || '#1677ff';
  const countries = [{ code: '+91', label: '🇮🇳 +91', value: '+91' }];

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '75vh',
        background: 'rgba(250,250,250,0.9)',
        padding: '24px',
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.25 }}
        style={{ width: '100%', maxWidth: 420 }}
      >
        <Card
          bordered={false}
          style={{
            borderRadius: 12,
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            overflow: 'hidden',
            position: 'relative',
          }}
          bodyStyle={{ padding: '32px 28px' }}
        >
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={onCancel}
            style={{ position: 'absolute', top: 16, right: 16, color: '#999', fontSize: 16 }}
          />

          {step === 'input' ? (
            <>
              <Title level={4} style={{ marginBottom: 8 }}>
                Sign in to continue
              </Title>
              <Text type="secondary">
                Enter your {method === 'phone' ? 'mobile number' : 'email address'} to receive a verification code.
              </Text>

              <div style={{ marginTop: 24 }}>
                {method === 'phone' ? (
                  <Input.Group compact>
                    <Select
                      value={countryCode}
                      onChange={(v) => setCountryCode(v)}
                      style={{ width: '30%' }}
                      size="large"
                                      className="form-control"

                    >
                      {countries.map((c) => (
                        <Option key={c.code} value={c.value}>
                          {c.label}
                        </Option>
                      ))}
                    </Select>
                    <Input
                      size="large"
                                      className="form-control"

                      placeholder="Phone number"
                      value={value}
                      onChange={(e) => setValue(e.target.value.replace(/\D/g, ''))}
                      maxLength={10}
                      style={{ width: '70%' }}
                    />
                  </Input.Group>
                ) : (
                  <Input
                    size="large"
                                    className="form-control"
                    placeholder="Email address"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                  />
                )}
              </div>

         <div className="modal-footer d-flex justify-content-center">

              
   
              
              <Button
                type="primary"
                className="btn btn-primary"
                block
                size="large"
                onClick={sendOTP}
                loading={loading}
                disabled={!validateInput()}
                style={{
                  marginTop: 24,
                  height: 45,
                  borderRadius: 6,
                  fontWeight: 600,
                  backgroundColor: primaryColor,
                  width : "70%",
                }}
              >
                Continue
              </Button>

              <Divider plain style={{ margin: '24px 0 12px' }}>
                OR
              </Divider>

<Button
  type="link"
  block
  onClick={switchMethod}
  style={{ 
    fontWeight: 500, 
    color: '#db3a46', 
    textDecoration: 'none', 
    transition: 'color 0.2s ease' 
  }}
  onMouseEnter={(e) => (e.target.style.color = '#a92634')}
  onMouseLeave={(e) => (e.target.style.color = '#db3a46')}
>
  Use {method === 'phone' ? 'email' : 'phone number'} instead
</Button>

             </div>   
            </>
          ) : (
            <>
              <Title level={4} style={{ marginBottom: 8 }}>
                Verify your {method}
              </Title>
              <Text type="secondary">
                We’ve sent a 6-digit code to{' '}
                <strong>{method === 'phone' ? `${countryCode} ${value}` : value}</strong>.
              </Text>

              <div style={{ marginTop: 24 }}>
                <Input.OTP
                  length={6}
                  value={otp}
                  onChange={setOtp}
                  style={{ justifyContent: 'space-between', marginBottom: 24 }}
                />
              </div>

              <Button
                type="primary"
                block
                size="large"
                loading={loading}
                disabled={otp.length !== 6}
                onClick={verifyOTP}
                style={{
                  height: 45,
                  borderRadius: 6,
                  fontWeight: 600,
                  backgroundColor: primaryColor,
                }}
              >
                Verify
              </Button>

              <Button
                type="link"
                block
                onClick={() => setStep('input')}
                style={{ marginTop: 12, color: primaryColor }}
              >
                Change {method}
              </Button>
            </>
          )}
        </Card>
      </motion.div>
    </div>
  );
};

export default AuthFlow;
